import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Form, Button } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import Spinner from "../generics/Spinner";

const getFrequencyStyle = (frequency) => {
  const styles = {
    DAILY: { backgroundColor: "#0d6efd", color: "#fff" },
    WEEKLY: { backgroundColor: "#6f42c1", color: "#fff" },
    BIWEEKLY: { backgroundColor: "#6610f2", color: "#fff" },
    MONTHLY: { backgroundColor: "#198754", color: "#fff" },
    QUARTERLY: { backgroundColor: "#fd7e14", color: "#fff" },
    SEMIANNUAL: { backgroundColor: "#ffc107", color: "#000" },
    ANNUAL: { backgroundColor: "#dc3545", color: "#fff" },
  };
  return styles[frequency] || { backgroundColor: "#6c757d", color: "#fff" };
};

const TaskAssignmentsList = ({
  assignments,
  templates,
  machines,
  facilities,
  loading,
  error,
  pages,
  currentPage,
  onFilter,
  onDelete,
  onGenerateTasks,
  onGenerateAllTasks,
  onPageChange,
}) => {
  const [filters, setFilters] = useState({
    template: "",
    machine: "",
    facility: "",
    isActive: "",
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      template: "",
      machine: "",
      facility: "",
      isActive: "",
    };
    setFilters(clearedFilters);
    if (onFilter) {
      onFilter(clearedFilters);
    }
  };

  const handleDelete = (assignment) => {
    const targetName = assignment.target_name || "this target";
    if (window.confirm(`Are you sure you want to delete the assignment for "${targetName}"?`)) {
      onDelete(assignment.id);
    }
  };

  const handleGenerateTasks = (assignment) => {
    if (window.confirm("Generate scheduled tasks from this assignment?")) {
      onGenerateTasks(assignment.id);
    }
  };

  const handleGenerateAll = () => {
    if (window.confirm("Generate scheduled tasks for ALL active assignments?")) {
      onGenerateAllTasks();
    }
  };

  if (loading && assignments.length === 0) {
    return <Spinner />;
  }

  return (
    <div className="task-assignments-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fa fa-link me-2"></i>
          Task Assignments
        </h4>
        <div>
          <Button
            variant="outline-success"
            size="sm"
            className="me-2"
            onClick={handleGenerateAll}
          >
            <i className="fa fa-refresh me-1"></i> Generate All Tasks
          </Button>
          <Link to="/maintenance/task-assignments/new" className="btn btn-primary btn-sm">
            <i className="fa fa-plus me-1"></i> Add Assignment
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-header reports-filter">
          <Form className="row align-items-center mb-0">
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label ml-05">
                Template
              </label>
              <select
                className="form-control"
                value={filters.template}
                onChange={(e) => handleFilterChange("template", e.target.value)}
              >
                <option value="">All Templates</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label ml-05">
                Machine
              </label>
              <select
                className="form-control"
                value={filters.machine}
                onChange={(e) => handleFilterChange("machine", e.target.value)}
              >
                <option value="">All Machines</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Facility
              </label>
              <select
                className="form-control"
                value={filters.facility}
                onChange={(e) => handleFilterChange("facility", e.target.value)}
              >
                <option value="">All Facilities</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Status
              </label>
              <select
                className="form-control"
                value={filters.isActive}
                onChange={(e) => handleFilterChange("isActive", e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="col-12 col-sm-2 d-flex align-items-end" style={{ paddingTop: "32px" }}>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                <i className="fa fa-times me-1"></i> Clear
              </button>
            </div>
          </Form>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading task assignments"}
        </div>
      )}

      {/* Results */}
      <Card>
        <Card.Body className="p-0">
          {assignments.length === 0 ? (
            <div className="shipment-list--empty text-center py-5">
              <i className="fa fa-link fa-3x mb-3 text-muted"></i>
              <p className="mb-0 text-muted">No task assignments found</p>
              <p className="text-muted small mt-2">
                Task assignments link templates to machines or facilities
              </p>
            </div>
          ) : (
            <table className="table table-striped table-bordered datatable mb-0">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Target</th>
                  <th>Frequency</th>
                  <th>Start Date</th>
                  <th>Last Generated</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: "140px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => {
                  const templateName = assignment.task_template_name || `Template #${assignment.task_template}`;
                  const targetName = assignment.target_name || "-";
                  const targetType = assignment.target_type;

                  return (
                  <tr key={assignment.id}>
                    <td>
                      <Link to={`/maintenance/task-templates/${assignment.task_template}`}>
                        <strong>{templateName}</strong>
                      </Link>
                    </td>
                    <td>
                      {targetType === "MACHINE" && assignment.machine ? (
                        <Link to={`/maintenance/machines/${assignment.machine}`}>
                          <i className="fa fa-cog me-1"></i>
                          {targetName}
                        </Link>
                      ) : targetType === "FACILITY" && assignment.facility ? (
                        <Link to={`/maintenance/facilities/${assignment.facility}`}>
                          <i className="fa fa-building me-1"></i>
                          {targetName}
                        </Link>
                      ) : (
                        <span className="text-muted">{targetName}</span>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge badge-pill"
                        style={getFrequencyStyle(assignment.frequency)}
                      >
                        {assignment.frequency_display || assignment.frequency || "-"}
                      </span>
                    </td>
                    <td>
                      {assignment.start_date ? (
                        new Date(assignment.start_date).toLocaleDateString()
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {assignment.last_generated_date ? (
                        new Date(assignment.last_generated_date).toLocaleDateString()
                      ) : (
                        <span className="text-muted">Never</span>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge badge-pill"
                        style={{
                          backgroundColor: assignment.is_active ? "#198754" : "#6c757d",
                          color: "#fff",
                        }}
                      >
                        {assignment.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="me-1"
                        onClick={() => handleGenerateTasks(assignment)}
                        title="Generate Tasks"
                      >
                        <i className="fa fa-refresh"></i>
                      </Button>
                      <Link
                        to={`/maintenance/task-assignments/${assignment.id}/edit`}
                        className="btn btn-sm btn-outline-primary me-1"
                        title="Edit"
                      >
                        <i className="fa fa-pencil"></i>
                      </Link>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(assignment)}
                        title="Delete"
                      >
                        <i className="fa fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {pages > 1 && (
        <ReactPaginate
          pageCount={pages}
          pageRangeDisplayed={2}
          marginPagesDisplayed={1}
          previousLabel="<"
          nextLabel=">"
          onPageChange={({ selected }) => onPageChange(selected + 1)}
          forcePage={currentPage - 1}
          disableInitialCallback={true}
          containerClassName="pagination"
          activeClassName="active"
        />
      )}

      {assignments.length > 0 && (
        <div className="text-muted small mt-2">
          Showing {assignments.length} assignment{assignments.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

export default TaskAssignmentsList;
